import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Clock, AlertTriangle, CheckCircle, Calendar, Edit2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MieiProgettiPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [progetti, setProgetti] = useState([]);
  const [progettiFiltered, setProgettiFiltered] = useState([]);
  const [filtroStato, setFiltroStato] = useState('tutti');
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedProgetto, setSelectedProgetto] = useState(null);
  const [oreTracciate, setOreTracciate] = useState({}); // Ore dal time tracking
  const [formData, setFormData] = useState({
    stato: '',
    data_fine_effettiva: '',
    ore_straordinario: 0,
    usa_straordinario: false
  });
  
  // Stati per gestione revisioni
  const [revisioniDialogOpen, setRevisioniDialogOpen] = useState(false);
  const [nuovaRevisioneForm, setNuovaRevisioneForm] = useState({
    descrizione: '',
    ore: '',
    data: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchMieiProgetti();
    fetchOreTracciate();
  }, []);

  useEffect(() => {
    // Applica filtro stato
    if (filtroStato === 'tutti') {
      setProgettiFiltered(progetti);
    } else {
      setProgettiFiltered(progetti.filter(p => p.stato === filtroStato));
    }
  }, [progetti, filtroStato]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchMieiProgetti = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/progetti/miei`, getAuthHeaders());
      setProgetti(response.data);
    } catch (error) {
      console.error('Errore caricamento progetti:', error);
      toast.error('Errore nel caricamento dei progetti');
    } finally {
      setLoading(false);
    }
  };

  const fetchOreTracciate = async () => {
    try {
      const response = await axios.get(`${API}/time-entries/per-progetto`, getAuthHeaders());
      setOreTracciate(response.data);
    } catch (error) {
      console.error('Errore caricamento ore tracciate:', error);
    }
  };

  // Funzione per ottenere le ore tracciate per un progetto
  const getOreTracciateProgetto = (progetto) => {
    // Cerca per codice progetto (priorità: codice_progetto, poi codice_cliente)
    const codiceMS = progetto.codice_progetto;
    const codiceCliente = progetto.codice_cliente;
    
    let oreData = null;
    if (codiceMS && oreTracciate[codiceMS]) {
      oreData = oreTracciate[codiceMS];
    } else if (codiceCliente && oreTracciate[codiceCliente]) {
      oreData = oreTracciate[codiceCliente];
    }
    
    if (oreData) {
      const oreTotali = oreData.ore_totali + (oreData.minuti_totali / 60);
      return {
        ore: Math.round(oreTotali * 10) / 10, // Arrotonda a 1 decimale
        entries: oreData.entries_count
      };
    }
    return null;
  };

  const handleEditStato = (progetto) => {
    setSelectedProgetto(progetto);
    // Calcola ore standard automatiche per pre-popolare il campo
    const oreAuto = calcolaOreLavorateInterno(progetto.data_inizio, progetto.data_fine_effettiva);
    const oreManualeSalvate = progetto.ore_standard_manuali;
    
    setFormData({
      stato: progetto.stato,
      data_fine_effettiva: progetto.data_fine_effettiva || '',
      ore_straordinario: progetto.ore_straordinario || 0,
      usa_straordinario: (progetto.ore_straordinario || 0) > 0,
      ore_standard_manuali: oreManualeSalvate !== undefined && oreManualeSalvate !== null ? oreManualeSalvate : oreAuto,
      usa_ore_manuali: oreManualeSalvate !== undefined && oreManualeSalvate !== null
    });
    setEditDialogOpen(true);
  };
  
  // Funzione interna per calcolo automatico (usata per pre-popolare)
  const calcolaOreLavorateInterno = (dataInizio, dataFine) => {
    if (!dataInizio || !dataFine) return 0;
    
    const inizio = new Date(dataInizio);
    const fine = new Date(dataFine);
    
    let giorni = 0;
    let current = new Date(inizio);
    
    while (current <= fine) {
      const dayOfWeek = current.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        giorni++;
      }
      current.setDate(current.getDate() + 1);
    }
    
    return giorni * 8;
  };
  
  const handleGestisciRevisioni = (progetto) => {
    setSelectedProgetto(progetto);
    setRevisioniDialogOpen(true);
  };

  // Funzione per ottenere le ore (manuali o calcolate)
  const calcolaOreLavorate = (dataInizio, dataFine) => {
    // Se l'utente ha impostato le ore manuali, usa quelle
    if (formData.usa_ore_manuali && formData.ore_standard_manuali !== undefined) {
      return parseFloat(formData.ore_standard_manuali) || 0;
    }
    
    // Altrimenti calcola automaticamente
    if (!dataInizio || !dataFine) return 0;
    
    const inizio = new Date(dataInizio);
    const fine = new Date(dataFine);
    
    let giorni = 0;
    let current = new Date(inizio);
    
    while (current <= fine) {
      const dayOfWeek = current.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) { // Escludi domenica (0) e sabato (6)
        giorni++;
      }
      current.setDate(current.getDate() + 1);
    }
    
    return giorni * 8; // 8 ore per giorno lavorativo
  };

  const handleSaveStato = async () => {
    try {
      const updateData = {
        stato: formData.stato
      };

      if (formData.data_fine_effettiva) {
        updateData.data_fine_effettiva = formData.data_fine_effettiva;
      }

      if (formData.usa_straordinario) {
        updateData.ore_straordinario = parseFloat(formData.ore_straordinario) || 0;
      } else {
        updateData.ore_straordinario = 0;
      }

      // Salva le ore standard manuali se impostate
      if (formData.usa_ore_manuali) {
        updateData.ore_standard_manuali = parseFloat(formData.ore_standard_manuali) || 0;
      } else {
        updateData.ore_standard_manuali = null; // Reset per usare calcolo automatico
      }

      await axios.put(
        `${API}/progetti/${selectedProgetto.id}`,
        updateData,
        getAuthHeaders()
      );

      toast.success('Stato progetto aggiornato con successo');
      setEditDialogOpen(false);
      fetchMieiProgetti();
    } catch (error) {
      console.error('Errore aggiornamento stato:', error);
      toast.error('Errore nell\'aggiornamento dello stato');
    }
  };

  const handleAggiungiRevisione = async () => {
    if (!nuovaRevisioneForm.descrizione || !nuovaRevisioneForm.ore || !nuovaRevisioneForm.data) {
      toast.error('Compila tutti i campi della revisione');
      return;
    }

    try {
      const params = new URLSearchParams({
        descrizione: nuovaRevisioneForm.descrizione,
        ore: parseFloat(nuovaRevisioneForm.ore),
        data: nuovaRevisioneForm.data
      });

      await axios.post(
        `${API}/progetti/${selectedProgetto.id}/revisioni?${params.toString()}`,
        {},
        getAuthHeaders()
      );

      toast.success('Revisione aggiunta con successo');
      setNuovaRevisioneForm({
        descrizione: '',
        ore: '',
        data: new Date().toISOString().split('T')[0]
      });
      fetchMieiProgetti();
    } catch (error) {
      console.error('Errore aggiunta revisione:', error);
      toast.error('Errore nell\'aggiunta della revisione');
    }
  };

  const handleEliminaRevisione = async (revisioneId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa revisione?')) {
      return;
    }

    try {
      await axios.delete(
        `${API}/progetti/${selectedProgetto.id}/revisioni/${revisioneId}`,
        getAuthHeaders()
      );

      toast.success('Revisione eliminata con successo');
      fetchMieiProgetti();
    } catch (error) {
      console.error('Errore eliminazione revisione:', error);
      toast.error('Errore nell\'eliminazione della revisione');
    }
  };

  const calcolaGiorniRimanenti = (dataFine) => {
    if (!dataFine) return null;
    const oggi = new Date();
    oggi.setHours(0, 0, 0, 0);
    const fine = new Date(dataFine);
    fine.setHours(0, 0, 0, 0);
    const diff = Math.ceil((fine - oggi) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const getStatoProgetto = (progetto) => {
    if (progetto.stato === 'completato') return 'completato';
    // Se in validazione con data fine effettiva, il conteggio è sospeso (non è in ritardo)
    if (progetto.stato === 'in_validazione' && progetto.data_fine_effettiva) return 'in_validazione';
    const giorni = calcolaGiorniRimanenti(progetto.data_fine_prevista);
    if (giorni === null) return 'senza_scadenza';
    if (giorni < 0) return 'in_ritardo';
    if (giorni <= 3) return 'urgente';
    return 'in_tempo';
  };

  const getStatoColor = (stato) => {
    const colors = {
      'completato': 'bg-green-100 border-green-300',
      'in_ritardo': 'bg-red-100 border-red-300',
      'urgente': 'bg-orange-100 border-orange-300',
      'in_tempo': 'bg-blue-100 border-blue-300',
      'senza_scadenza': 'bg-slate-100 border-slate-300',
      'in_validazione': 'bg-purple-100 border-purple-300'
    };
    return colors[stato] || colors.in_tempo;
  };

  const getStatoLabel = (stato) => {
    const labels = {
      'non_iniziato': 'Non Iniziato',
      'in_corso': 'In Corso',
      'in_validazione': 'In Validazione',
      'in_revisione': 'In Revisione',
      'completato': 'Completato',
      'as_built': 'As Built',
      'sospeso': 'Sospeso'
    };
    return labels[stato] || stato;
  };

  const getStatoBadgeColor = (stato) => {
    const colors = {
      'non_iniziato': 'bg-slate-100 text-slate-700',
      'in_corso': 'bg-blue-100 text-blue-700',
      'in_validazione': 'bg-purple-100 text-purple-700',
      'in_revisione': 'bg-orange-100 text-orange-700',
      'completato': 'bg-green-100 text-green-700',
      'as_built': 'bg-emerald-100 text-emerald-700',
      'sospeso': 'bg-red-100 text-red-700'
    };
    return colors[stato] || 'bg-slate-100 text-slate-700';
  };

  const getStatoIcon = (stato) => {
    if (stato === 'completato' || stato === 'as_built') return <CheckCircle className="w-5 h-5 text-green-600" />;
    if (stato === 'in_ritardo') return <AlertTriangle className="w-5 h-5 text-red-600" />;
    if (stato === 'urgente') return <Clock className="w-5 h-5 text-orange-600" />;
    if (stato === 'in_validazione') return <Clock className="w-5 h-5 text-purple-600" />;
    return <Clock className="w-5 h-5 text-blue-600" />;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('it-IT');
  };

  const progettiInRitardo = progetti.filter(p => getStatoProgetto(p) === 'in_ritardo').length;
  const progettiUrgenti = progetti.filter(p => getStatoProgetto(p) === 'urgente').length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 py-3 sm:px-6 sm:py-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <Button variant="ghost" onClick={() => navigate('/')}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl sm:text-3xl font-bold text-slate-900">I Miei Progetti</h1>
                <p className="text-sm text-slate-600">Dashboard Personale - {user.nome} {user.cognome}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-3 py-4 sm:px-6 sm:py-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm text-slate-600">Totale Progetti</p>
                <p className="text-xl sm:text-3xl font-bold text-slate-900">{progetti.length}</p>
              </div>
              <Calendar className="w-10 h-10 text-blue-600" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm text-slate-600">In Corso</p>
                <p className="text-xl sm:text-3xl font-bold text-blue-600">
                  {progetti.filter(p => p.stato === 'in_corso').length}
                </p>
              </div>
              <Clock className="w-10 h-10 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm text-slate-600">Urgenti</p>
                <p className="text-xl sm:text-3xl font-bold text-orange-600">{progettiUrgenti}</p>
              </div>
              <Clock className="w-10 h-10 text-orange-600" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-sm text-slate-600">In Ritardo</p>
                <p className="text-xl sm:text-3xl font-bold text-red-600">{progettiInRitardo}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-red-600" />
            </div>
          </div>
        </div>

        {/* Lista Progetti */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-600">Caricamento...</p>
          </div>
        ) : progetti.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
            <p className="text-slate-600">Nessun progetto assegnato</p>
          </div>
        ) : (
          <>
            {/* Filtro Stato */}
            <div className="mb-4 flex items-center gap-4 bg-white rounded-xl p-4 shadow-sm border">
              <label className="text-sm font-medium text-slate-700">Filtra per stato:</label>
              <Select value={filtroStato} onValueChange={setFiltroStato}>
                <SelectTrigger className="w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tutti">Tutti</SelectItem>
                  <SelectItem value="non_iniziato">Non Iniziato</SelectItem>
                  <SelectItem value="in_corso">In Corso</SelectItem>
                  <SelectItem value="in_validazione">In Validazione</SelectItem>
                  <SelectItem value="in_revisione">In Revisione</SelectItem>
                  <SelectItem value="completato">Completato</SelectItem>
                  <SelectItem value="as_built">As Built</SelectItem>
                  <SelectItem value="sospeso">Sospeso</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-slate-600">
                {progettiFiltered.length} progett{progettiFiltered.length === 1 ? 'o' : 'i'}
              </span>
            </div>

            <div className="space-y-4">
            {progettiFiltered.map((progetto) => {
              const statoProgetto = getStatoProgetto(progetto);
              const giorni = calcolaGiorniRimanenti(progetto.data_fine_prevista);

              return (
                <div
                  key={progetto.id}
                  className={`bg-white rounded-xl shadow-sm border-2 p-6 ${getStatoColor(statoProgetto)}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {getStatoIcon(statoProgetto)}
                        <h3 className="text-xl font-bold text-slate-900">{progetto.nome_progetto}</h3>
                        <span className="px-3 py-1 bg-white rounded-full text-xs font-medium">
                          {progetto.numero_progetto}
                        </span>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatoBadgeColor(progetto.stato)}`}>
                          {getStatoLabel(progetto.stato)}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                        <div>
                          <p className="text-xs text-slate-500">Cliente</p>
                          <p className="font-medium text-sm">{progetto.cliente_nome}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Codice Cliente</p>
                          <p className="font-medium text-sm font-mono text-blue-600">{progetto.codice_progetto_cliente || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Codice Main Solution</p>
                          <p className="font-medium text-sm font-mono text-purple-600">{progetto.codice_progetto_main_solution || '-'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Tipo Impianto</p>
                          <p className="font-medium text-sm">{progetto.tipo_impianto}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Data Inizio</p>
                          <p className="font-medium text-sm">{formatDate(progetto.data_inizio)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Scadenza</p>
                          <p className="font-medium text-sm">{formatDate(progetto.data_fine_prevista)}</p>
                        </div>
                      </div>

                      {/* Riepilogo Ore se disponibili */}
                      {(() => {
                        const oreRevisioni = (progetto.revisioni || []).reduce((sum, rev) => sum + (rev.ore || 0), 0);
                        // Usa ore manuali se presenti, altrimenti ore calcolate
                        const oreStandardEffettive = progetto.ore_standard_manuali !== null && progetto.ore_standard_manuali !== undefined 
                          ? progetto.ore_standard_manuali 
                          : (progetto.ore_lavorate_standard || 0);
                        const isManuale = progetto.ore_standard_manuali !== null && progetto.ore_standard_manuali !== undefined;
                        
                        // Ore dal time tracking
                        const oreTracciateData = getOreTracciateProgetto(progetto);
                        const oreDalTracking = oreTracciateData ? oreTracciateData.ore : 0;
                        
                        const haOre = oreStandardEffettive > 0 || progetto.ore_straordinario > 0 || oreRevisioni > 0 || oreDalTracking > 0;
                        
                        return haOre && (
                          <div className="mt-4 p-3 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg">
                            <p className="text-xs font-semibold text-slate-700 mb-2">⏱️ Ore Lavorate</p>
                            <div className="flex flex-wrap gap-3 text-xs">
                              {/* Ore dal Time Tracking - evidenziate */}
                              {oreDalTracking > 0 && (
                                <div className="px-2 py-1 bg-green-100 rounded border border-green-400">
                                  <span className="text-green-800">🕐 Tracciate: </span>
                                  <span className="font-bold text-green-900">{oreDalTracking}h</span>
                                  <span className="text-green-600 ml-1">({oreTracciateData.entries} reg.)</span>
                                </div>
                              )}
                              {oreStandardEffettive > 0 && (
                                <div>
                                  <span className="text-slate-600">Standard{isManuale ? ' (manuale)' : ''}: </span>
                                  <span className="font-bold text-blue-700">{oreStandardEffettive}h</span>
                                </div>
                              )}
                              {progetto.ore_straordinario > 0 && (
                                <div>
                                  <span className="text-slate-600">Straordinario: </span>
                                  <span className="font-bold text-orange-700">{progetto.ore_straordinario}h</span>
                                </div>
                              )}
                              {oreRevisioni > 0 && (
                                <div className="px-2 py-1 bg-purple-200 rounded border border-purple-400">
                                  <span className="text-purple-800 font-bold">⚙️ Revisioni: </span>
                                  <span className="font-bold text-purple-900">{oreRevisioni}h ({progetto.revisioni.length})</span>
                                </div>
                              )}
                              <div className="ml-auto">
                                <span className="text-slate-600">Totale: </span>
                                <span className="font-bold text-lg text-slate-900">
                                  {(oreDalTracking || oreStandardEffettive) + (progetto.ore_straordinario || 0) + oreRevisioni}h
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    <div className="text-right ml-4 flex flex-col gap-3">
                      <Button
                        size="sm"
                        onClick={() => handleEditStato(progetto)}
                        className="bg-indigo-600 hover:bg-indigo-700"
                      >
                        <Edit2 className="w-4 h-4 mr-2" />
                        Modifica Stato
                      </Button>
                      
                      <Button
                        size="sm"
                        onClick={() => handleGestisciRevisioni(progetto)}
                        className="bg-purple-600 hover:bg-purple-700"
                      >
                        ⚙️ Revisioni ({progetto.revisioni?.length || 0})
                      </Button>
                      
                      {giorni !== null && progetto.stato !== 'completato' && (
                        <div className="text-center">
                          <div className={`text-4xl font-bold ${
                            giorni < 0 ? 'text-red-600' : 
                            giorni <= 3 ? 'text-orange-600' : 
                            'text-blue-600'
                          }`}>
                            {Math.abs(giorni)}
                          </div>
                          <p className="text-xs text-slate-600">
                            {giorni < 0 ? 'giorni di ritardo' : 'giorni rimanenti'}
                          </p>
                        </div>
                      )}
                      {progetto.stato === 'completato' && (
                        <div className="text-center">
                          <CheckCircle className="w-12 h-12 text-green-600 mx-auto" />
                          <p className="text-xs text-slate-600 mt-1">Completato</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {statoProgetto === 'in_ritardo' && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-700 font-medium flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        PROGETTO IN RITARDO - Admin e Master sono stati notificati
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          </>
        )}
      </div>

      {/* Dialog Modifica Stato */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifica Stato Progetto</DialogTitle>
          </DialogHeader>
          
          {selectedProgetto && (
            <div className="space-y-6">
              <div className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-semibold text-slate-900">{selectedProgetto.nome_progetto}</h3>
                <p className="text-sm text-slate-600">{selectedProgetto.numero_progetto} - {selectedProgetto.cliente_nome}</p>
              </div>

              {/* Stato */}
              <div>
                <Label htmlFor="stato">Stato Progetto *</Label>
                <Select
                  value={formData.stato}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, stato: value }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="non_iniziato">Non Iniziato</SelectItem>
                    <SelectItem value="in_corso">In Corso</SelectItem>
                    <SelectItem value="in_validazione">In Validazione</SelectItem>
                    <SelectItem value="in_revisione">In Revisione</SelectItem>
                    <SelectItem value="completato">Completato</SelectItem>
                    <SelectItem value="as_built">As Built</SelectItem>
                    <SelectItem value="sospeso">Sospeso</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Data Fine Effettiva */}
              <div>
                <Label htmlFor="data_fine_effettiva">Data Fine Effettiva</Label>
                <Input
                  id="data_fine_effettiva"
                  type="date"
                  value={formData.data_fine_effettiva}
                  onChange={(e) => {
                    const newDataFine = e.target.value;
                    // Ricalcola ore automatiche quando cambia la data
                    const oreAuto = calcolaOreLavorateInterno(selectedProgetto.data_inizio, newDataFine);
                    setFormData(prev => ({ 
                      ...prev, 
                      data_fine_effettiva: newDataFine,
                      // Se non usa ore manuali, aggiorna il valore
                      ore_standard_manuali: prev.usa_ore_manuali ? prev.ore_standard_manuali : oreAuto
                    }));
                  }}
                  className="mt-1"
                />
              </div>

              {/* Sezione Ore Lavorate */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm font-semibold text-blue-900 mb-3">📊 Ore Lavorate</p>
                
                {/* Toggle per ore manuali */}
                <div className="flex items-center gap-2 mb-3">
                  <input
                    type="checkbox"
                    id="usa_ore_manuali"
                    checked={formData.usa_ore_manuali}
                    onChange={(e) => {
                      const usaManuali = e.target.checked;
                      if (!usaManuali) {
                        // Torna al calcolo automatico
                        const oreAuto = calcolaOreLavorateInterno(selectedProgetto.data_inizio, formData.data_fine_effettiva);
                        setFormData(prev => ({ ...prev, usa_ore_manuali: false, ore_standard_manuali: oreAuto }));
                      } else {
                        setFormData(prev => ({ ...prev, usa_ore_manuali: true }));
                      }
                    }}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <Label htmlFor="usa_ore_manuali" className="cursor-pointer text-sm text-blue-700">
                    Inserisci ore manualmente
                  </Label>
                </div>

                {/* Campo Ore */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="ore_standard" className="text-xs text-blue-700">
                      {formData.usa_ore_manuali ? 'Ore Standard (inserite)' : 'Ore Standard (calcolate)'}:
                    </Label>
                    {formData.usa_ore_manuali ? (
                      <Input
                        id="ore_standard"
                        type="number"
                        step="0.5"
                        min="0"
                        value={formData.ore_standard_manuali}
                        onChange={(e) => setFormData(prev => ({ ...prev, ore_standard_manuali: e.target.value }))}
                        className="mt-1 text-xl font-bold"
                        placeholder="Inserisci ore"
                      />
                    ) : (
                      <p className="text-2xl font-bold text-blue-900 mt-1">
                        {formData.data_fine_effettiva && selectedProgetto.data_inizio 
                          ? `${calcolaOreLavorateInterno(selectedProgetto.data_inizio, formData.data_fine_effettiva)}h`
                          : '0h'}
                      </p>
                    )}
                    {!formData.usa_ore_manuali && (
                      <p className="text-xs text-slate-500 mt-1">8h/giorno lavorativo</p>
                    )}
                  </div>
                  {formData.usa_straordinario && (
                    <div>
                      <p className="text-xs text-orange-700">Ore Straordinario:</p>
                      <p className="text-2xl font-bold text-orange-900 mt-1">
                        {formData.ore_straordinario}h
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Totale */}
                <div className="mt-3 pt-3 border-t border-blue-300">
                  <p className="text-xs text-blue-700">Totale Ore Complessive:</p>
                  <p className="text-3xl font-bold text-blue-900">
                    {(parseFloat(formData.ore_standard_manuali) || 0) + 
                     (formData.usa_straordinario ? parseFloat(formData.ore_straordinario || 0) : 0)}h
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Note: Le revisioni vengono gestite separatamente tramite il pulsante &quot;Revisioni&quot;
                  </p>
                </div>
              </div>

              {/* Checkbox Straordinario */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="usa_straordinario"
                  checked={formData.usa_straordinario}
                  onChange={(e) => setFormData(prev => ({ ...prev, usa_straordinario: e.target.checked }))}
                  className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                />
                <Label htmlFor="usa_straordinario" className="cursor-pointer">
                  Aggiungi ore straordinario
                </Label>
              </div>

              {/* Ore Straordinario */}
              {formData.usa_straordinario && (
                <div>
                  <Label htmlFor="ore_straordinario">Ore Straordinario</Label>
                  <Input
                    id="ore_straordinario"
                    type="number"
                    step="0.5"
                    value={formData.ore_straordinario}
                    onChange={(e) => setFormData(prev => ({ ...prev, ore_straordinario: e.target.value }))}
                    className="mt-1"
                    placeholder="0"
                  />
                </div>
              )}

              {/* Pulsanti */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setEditDialogOpen(false)}
                >
                  Annulla
                </Button>
                <Button
                  onClick={handleSaveStato}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  Salva Modifiche
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Gestione Revisioni */}
      <Dialog open={revisioniDialogOpen} onOpenChange={setRevisioniDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Gestione Revisioni Progetto</DialogTitle>
          </DialogHeader>
          
          {selectedProgetto && (
            <div className="space-y-6">
              <div className="p-4 bg-slate-50 rounded-lg">
                <h3 className="font-semibold text-slate-900">{selectedProgetto.nome_progetto}</h3>
                <p className="text-sm text-slate-600">{selectedProgetto.numero_progetto} - {selectedProgetto.cliente_nome}</p>
              </div>

              {/* Lista Revisioni Esistenti */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-3">Revisioni Esistenti</h4>
                {selectedProgetto.revisioni && selectedProgetto.revisioni.length > 0 ? (
                  <div className="space-y-2">
                    {selectedProgetto.revisioni.map((rev) => (
                      <div key={rev.id} className="p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="px-2 py-1 bg-purple-600 text-white text-xs font-bold rounded">
                              Rev. {rev.numero}
                            </span>
                            <span className="text-xs text-slate-500">
                              {new Date(rev.data).toLocaleDateString('it-IT')}
                            </span>
                            <span className="text-xs font-bold text-purple-700">
                              {rev.ore}h
                            </span>
                          </div>
                          <p className="text-sm text-slate-700">{rev.descrizione}</p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEliminaRevisione(rev.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          ✕
                        </Button>
                      </div>
                    ))}
                    <div className="p-3 bg-purple-100 border-2 border-purple-300 rounded-lg">
                      <p className="text-sm font-bold text-purple-900">
                        Totale Ore Revisioni: {selectedProgetto.revisioni.reduce((sum, rev) => sum + (rev.ore || 0), 0)}h
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="p-6 bg-slate-50 border border-slate-200 rounded-lg text-center">
                    <p className="text-slate-500 text-sm">Nessuna revisione presente</p>
                  </div>
                )}
              </div>

              {/* Form Nuova Revisione */}
              <div className="border-t pt-6">
                <h4 className="font-semibold text-slate-900 mb-3">Aggiungi Nuova Revisione</h4>
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="descrizione_revisione">Descrizione *</Label>
                    <Input
                      id="descrizione_revisione"
                      type="text"
                      value={nuovaRevisioneForm.descrizione}
                      onChange={(e) => setNuovaRevisioneForm(prev => ({ ...prev, descrizione: e.target.value }))}
                      placeholder="Es: Correzione layout interfaccia"
                      className="mt-1"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="ore_revisione">Ore Lavorate *</Label>
                      <Input
                        id="ore_revisione"
                        type="number"
                        step="0.5"
                        value={nuovaRevisioneForm.ore}
                        onChange={(e) => setNuovaRevisioneForm(prev => ({ ...prev, ore: e.target.value }))}
                        placeholder="0"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="data_revisione">Data *</Label>
                      <Input
                        id="data_revisione"
                        type="date"
                        value={nuovaRevisioneForm.data}
                        onChange={(e) => setNuovaRevisioneForm(prev => ({ ...prev, data: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <Button
                    onClick={handleAggiungiRevisione}
                    className="w-full bg-purple-600 hover:bg-purple-700"
                  >
                    ⚙️ Aggiungi Revisione
                  </Button>
                </div>
              </div>

              {/* Pulsante Chiudi */}
              <div className="flex justify-end pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setRevisioniDialogOpen(false)}
                >
                  Chiudi
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MieiProgettiPage;
